/**
 * The brand mark used in the CRM chrome: the operator's own logo when one is set
 * under Settings -> Appearance, otherwise the built-in WebAdmin logo.
 *
 * Two marks, not one. The wide logo is what the header shows, but the collapsed
 * sidebar is 58px across and would cut a wordmark in half, so there is a second,
 * square mark for that state. `mark="auto"` renders both and lets CSS pick by
 * the `html.crm-mini` class - the same class the rail and the content grid
 * already follow, so the collapsed state keeps one source of truth. A phone's
 * top bar picks the square mark too, by width (`crm-topbar.css`).
 *
 * A dark variant is optional in both cases - with only one file configured the
 * same image is used in both themes. `className` styles the built-in fallback
 * only; a custom logo is sized by the configured height, because that is what
 * the operator set.
 */
import { useBranding } from "./BrandingProvider";
import { Logo } from "./Logo";
import {
  CRM_ICON_HEIGHT_DEFAULT, CRM_LOGO_HEIGHT_DEFAULT,
  hasCustomIcon, isDefaultBranding, type CrmBranding,
} from "../lib/branding";

export interface BrandLogoProps {
  /** Classes for the built-in fallback logo (e.g. "h-6 w-auto"). */
  className?: string;
  /** Render this value instead of the stored one - used for the settings preview. */
  branding?: CrmBranding;
  /**
   * Which mark to draw. "auto" renders both and lets the collapsed-rail class
   * choose; the fixed values are for the places that are never collapsed (login,
   * access notice) and for the settings preview, which shows both side by side.
   */
  mark?: "full" | "icon" | "auto";
}

/** One image, or a light/dark pair when the two files differ. */
function Img({
  light, dark, alt, height, cap,
}: {
  light: string;
  dark: string;
  alt: string;
  height: number;
  /**
   * The single width ceiling, so an unusually wide file cannot push the row
   * apart. One max-width utility only: a second one (`max-w-full`) would not
   * "also apply" - whichever Tailwind emits last silently wins.
   */
  cap: string;
}) {
  const style = { height, width: "auto" as const };
  const base = `${cap} object-contain`;
  if (light === dark) {
    return <img src={light} alt={alt} style={style} className={base} draggable={false} />;
  }
  return (
    <>
      <img src={light} alt={alt} style={style} className={`${base} dark:hidden`} draggable={false} />
      <img src={dark} alt={alt} style={style} className={`hidden ${base} dark:block`} draggable={false} />
    </>
  );
}

function FullMark({ branding, className }: { branding: CrmBranding; className?: string }) {
  if (isDefaultBranding(branding)) return <Logo variant="full" className={className} />;
  return (
    <Img
      light={branding.logoUrl || branding.logoDarkUrl}
      dark={branding.logoDarkUrl || branding.logoUrl}
      alt={branding.alt || "WebAdmin"}
      height={branding.logoHeight || CRM_LOGO_HEIGHT_DEFAULT}
      cap="max-w-[220px]"
    />
  );
}

/**
 * The collapsed-rail mark. With no square file uploaded the wide logo is still
 * drawn, only boxed: `object-contain` inside the 44px column shrinks it to fit
 * whole rather than cropping it, which is the failure this mark exists to fix.
 */
function IconMark({ branding }: { branding: CrmBranding }) {
  const height = branding.iconHeight || CRM_ICON_HEIGHT_DEFAULT;
  const alt = branding.alt || "WebAdmin";

  if (hasCustomIcon(branding)) {
    return (
      <Img
        light={branding.iconUrl || branding.iconDarkUrl}
        dark={branding.iconDarkUrl || branding.iconUrl}
        alt={alt}
        height={height}
        cap="max-w-[44px]"
      />
    );
  }
  if (!isDefaultBranding(branding)) {
    return (
      <Img
        light={branding.logoUrl || branding.logoDarkUrl}
        dark={branding.logoDarkUrl || branding.logoUrl}
        alt={alt}
        height={height}
        cap="max-w-[44px]"
      />
    );
  }
  return <Logo variant="icon" className="h-[22px] w-auto text-foreground" />;
}

export function BrandLogo({ className, branding: override, mark = "full" }: BrandLogoProps) {
  const ctx = useBranding();
  const branding = override ?? ctx.branding;

  if (mark === "icon") {
    return (
      <span className="crm-brand-mini">
        <IconMark branding={branding} />
      </span>
    );
  }
  if (mark === "full") return <FullMark branding={branding} className={className} />;

  // Both marks are in the DOM; `html.crm-mini` decides which one is painted.
  return (
    <>
      <span className="crm-brand-wide">
        <FullMark branding={branding} className={className} />
      </span>
      <span className="crm-brand-mini is-swap">
        <IconMark branding={branding} />
      </span>
    </>
  );
}
